import React, { useRef } from 'react';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import CollapsibleRowMenu, {
  CollapsibleRowMenuButton,
  CollapsibleRowMenuDivider,
} from 'components/collapsible-row-menu';
import ToggleQuestionTypeButton from './toggle-question-type';
import ToggleResponseVisibilityButton from './toggle-response-visibility';
import MoveButtons from './move-buttons';
import { MoveControls, QuestionTypeControls, VisibilityControls } from './types';

type OptionsMenuProps = {
  deleteControls: () => void;
  moveControls: MoveControls;
  questionTypeControls: QuestionTypeControls;
  visibilityControls?: VisibilityControls;
};

const OptionsMenu = ({
  deleteControls,
  moveControls,
  questionTypeControls,
  visibilityControls,
}: OptionsMenuProps) => {
  const rowMenuRef = useRef<HTMLDivElement>(null);
  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS;

  const menuItems = [
    {
      key: 'delete',
      item: (
        <CollapsibleRowMenuButton
          icon='trash'
          onClick={deleteControls}
          data-qa={
            config.pendo.athena.mentorshipProgram.settings.programProfileSetup
              .deleteProgramLevelProfileQuestion
          }
          aria-label={translationBase.ARIA_LABEL.DELETE_PROGRAM_LEVEL_QUESTION()}
        />
      ),
    },
    {
      key: 'move',
      item: (
        <MoveButtons
          moveControls={moveControls}
          rowMenuRef={rowMenuRef}
        />
      ),
    },
    {
      key: 'toggle-type',
      item: (
        <ToggleQuestionTypeButton
          mode={questionTypeControls.mode}
          onClick={questionTypeControls.onToggle}
        />
      ),
    },
    visibilityControls && {
      key: 'visibility',
      item: (
        <ToggleResponseVisibilityButton
          hideMentorResponse={visibilityControls.hidden}
          disabled={visibilityControls.disabled}
          onClick={visibilityControls.onToggle}
        />
      ),
    },
  ].filter(Boolean) as { key: string; item: React.ReactNode }[];

  return (
    <CollapsibleRowMenu
      className='self-centered'
      ref={rowMenuRef}
      ariaLabel={translationBase.ARIA_LABEL.PROGRAM_QUESTION_OPTIONS_MENU()}
    >
      {menuItems.map(({ key, item }, i) => (
        <React.Fragment key={key}>
          {i > 0 && <CollapsibleRowMenuDivider />}
          {item}
        </React.Fragment>
      ))}
    </CollapsibleRowMenu>
  );
};

export default OptionsMenu;
